import { handleGlobalError } from '@shared/services/errorHandler'
import { useConfig } from '@shared/stores/config.js'
import { useWss } from '@shared/stores/wss'
import { Howl } from 'howler'
import { ref, watch } from 'vue'
import { toast } from 'vue3-toastify'

import router from '@/router'

export function useOperationMonitor() {
  const wssStore = useWss()

  const operationTypes = {
    PRE_OPERATION: 'preOperation',
    POS_OPERATION: 'posOperation',
    NEW_CLIENT: 'newClient'
  }

  const formatType = ref({
    BUYING: { name: 'COMPRA', color: '#6195e1' },
    SELLING: { name: 'VENDA', color: '#71cc98' }
  })

  const formatCurrency = (amount, currencyCode = 'BRL') => {
    try {
      const number = parseFloat(amount)

      const formatter = new Intl.NumberFormat('pt-BR', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })

      return `${currencyCode} ${formatter.format(number)}`
    } catch (e) {
      handleGlobalError(e, 'Erro formatCurrency:')
      return `${currencyCode} ${amount}`
    }
  }

  const onToastClick = (origin, operationId) => {
    try {
      const queryParams = { id: operationId }
      toast.clearAll()

      let routeName = ''

      if (origin === 'preOperation') routeName = 'PreOperations'
      else if (origin === 'posOperation') routeName = 'PosOperations'
      else if (origin === 'newClient') routeName = 'Clients'

      router.push({ name: routeName, query: queryParams })
    } catch (e) {
      handleGlobalError(e, 'Erro onToastClick:')
    }
  }

  const soundMap = {
    [operationTypes.PRE_OPERATION]: import.meta.env.VITE_S3_URL + '/sfxs/newPreOperationAlert.mp3',
    [operationTypes.POS_OPERATION]: import.meta.env.VITE_S3_URL + '/sfxs/newPosOperationAlert.mp3',
    [operationTypes.NEW_CLIENT]: import.meta.env.VITE_S3_URL + '/sfxs/newClientAlert.mp3',
    posStablecoin: import.meta.env.VITE_S3_URL + '/sfxs/newPosOperationStablecoinAlert.mp3'
  }

  const playSoundAndToast = (origin, operation, message) => {
    try {
      let alert = ''

      if (origin === 'preOperation' || origin === 'posOperation') {
        const formattedAmount = formatCurrency(operation.principalAmount, operation.currencyType)
        const typeInfo = formatType.value[operation.type]
        alert = `${message}\n${operation.clientName}\n${formattedAmount} - <b style="color: ${typeInfo.color};">${typeInfo.name}</b>`
      } else {
        alert = `${message}\n${operation.clientName}`
      }

      toast(alert, {
        autoClose: 10000,
        theme: 'auto',
        type: 'success',
        dangerouslyHTMLString: true,
        onClick: () => onToastClick(origin, operation.id)
      })

      const config = useConfig()

      if (config.sound) {
        let audioSource = soundMap[origin]

        if (
          origin === operationTypes.POS_OPERATION &&
          ['USDC', 'USDT'].includes(operation.currencyType)
        ) {
          audioSource = soundMap.posStablecoin
        }

        const sound = new Howl({
          src: [audioSource],
          autoplay: true,
          volume: 1.0,
          onloaderror: (id, e) => {
            handleGlobalError(e, 'Erro carregando áudio:')
          },
          onplayerror: (id, e) => {
            handleGlobalError(e, 'Erro ao reproduzir áudio:')
          }
        })

        sound.play()
      }
    } catch (e) {
      handleGlobalError(e, 'Erro playSoundAndToast:')
    }
  }

  watch(
    () => wssStore.getNewPreOperation.id,
    (newVal, oldVal) => {
      try {
        if (newVal && newVal !== oldVal) {
          playSoundAndToast(
            operationTypes.PRE_OPERATION,
            wssStore.getNewPreOperation,
            'Nova operação criada'
          )
        }
      } catch (e) {
        handleGlobalError(e, 'Erro watcher PRE_OPERATION:')
      }
    }
  )

  watch(
    () => wssStore.getNewPosOperation.id,
    (newVal, oldVal) => {
      try {
        if (newVal && newVal !== oldVal) {
          playSoundAndToast(
            operationTypes.POS_OPERATION,
            wssStore.getNewPosOperation,
            'Nova operação fechada'
          )
        }
      } catch (e) {
        handleGlobalError(e, 'Erro watcher POS_OPERATION:')
      }
    }
  )

  watch(
    () => wssStore.getNewClient.id,
    (newVal, oldVal) => {
      try {
        if (newVal && newVal !== oldVal) {
          playSoundAndToast(operationTypes.NEW_CLIENT, wssStore.getNewClient, 'Novo cliente')
        }
      } catch (e) {
        handleGlobalError(e, 'Erro watcher NEW_CLIENT:')
      }
    }
  )
}
